// VoiceSplitAdmin.jsx — Abonnements TeckiA Voice Kar (illimité 20 000 Ar / 30 jours)
// Paiements MVola / Orange Money déclarés par les clients -> validation ou refus (motif obligatoire,
// envoyé au client dans sa boîte de réception).

import React, { useState, useEffect, useCallback } from 'react';
import { Mic, Loader2, RefreshCw, CheckCircle, XCircle, Crown } from 'lucide-react';
import { API_URL } from '../../config';
import PaymentMethodBadge from '../shared/PaymentMethodBadge';

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const METHOD_LABELS = { mvola: 'MVola', orange: 'Orange Money' };
const FILTERS = [
  { key: 'pending', label: 'En attente' },
  { key: 'validated', label: 'Validés' },
  { key: 'rejected', label: 'Refusés' },
];

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';
const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

export default function VoiceSplitAdmin() {
  const [filter, setFilter] = useState('pending');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/voice-split/admin/orders?status=${filter}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setOrders(data.orders); else setError(data.message || 'Chargement impossible');
    } catch (e) { setError('Chargement impossible'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const validate = async (id) => {
    setActingOn(id); setError('');
    try {
      const res = await fetch(`${API_URL}/api/voice-split/admin/orders/${id}/validate`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!data.success) setError(data.message || 'Validation impossible');
      await load();
    } finally { setActingOn(null); }
  };

  const reject = async (id) => {
    const note = window.prompt('Raison du refus (envoyée au client dans sa messagerie) :', 'Paiement non retrouvé');
    if (note === null || !note.trim()) return;
    setActingOn(id); setError('');
    try {
      const res = await fetch(`${API_URL}/api/voice-split/admin/orders/${id}/reject`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ note: note.trim() }),
      });
      const data = await res.json();
      if (!data.success) setError(data.message || 'Refus impossible');
      await load();
    } finally { setActingOn(null); }
  };

  return (
    <div className="p-4 md:p-8 text-white max-w-4xl">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2.5">
          <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center"><Mic className="w-5 h-5" /></span>
          Voice Kar — Abonnements
        </h1>
        <button onClick={load} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition" title="Actualiser">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="text-sm text-blue-200 mb-4">
        Illimité = 20 000 Ar pour 30 jours (un renouvellement s'ajoute à la fin de l'abonnement en cours). Sans abonnement : 10 séparations gratuites par mois.
      </p>

      <div className="flex gap-2 mb-5">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-4 py-1.5 rounded-full text-sm border transition ${filter === f.key ? 'bg-blue-500/30 border-blue-400/60' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="mb-4 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">{error}</p>}

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <p className="text-gray-400 text-sm py-8 text-center">Aucune commande {FILTERS.find(f => f.key === filter).label.toLowerCase()}.</p>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <div key={o.$id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold break-all flex items-center gap-2 flex-wrap">{o.user_email || o.user_id} <PaymentMethodBadge method={o.method} /></p>
                <p className="text-xs text-blue-200 mt-0.5">
                  {Number(o.amount_ar).toLocaleString('fr-FR')} Ar · réf. <b>{o.payment_ref}</b>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">Déclaré le {fmtDate(o.created_at)}</p>
                {o.status === 'validated' && (
                  <p className="text-xs text-emerald-300 mt-0.5 flex items-center gap-1"><Crown className="w-3 h-3" /> Actif jusqu'au {fmtDay(o.expires_at)}</p>
                )}
                {o.status === 'rejected' && o.admin_note && <p className="text-xs text-rose-300 mt-0.5">Motif : {o.admin_note}</p>}
              </div>
              {o.status === 'pending' && (
                <div className="flex gap-2">
                  <button onClick={() => validate(o.$id)} disabled={actingOn === o.$id}
                    className="px-3.5 py-2 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-sm font-semibold hover:bg-emerald-500/30 disabled:opacity-40 transition flex items-center gap-1.5">
                    {actingOn === o.$id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Valider
                  </button>
                  <button onClick={() => reject(o.$id)} disabled={actingOn === o.$id}
                    className="px-3.5 py-2 rounded-xl bg-rose-500/20 border border-rose-500/40 text-rose-200 text-sm font-semibold hover:bg-rose-500/30 disabled:opacity-40 transition flex items-center gap-1.5">
                    <XCircle className="w-4 h-4" /> Refuser
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
